import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncAnalysis } from '../../hooks/useAsyncAnalysis';
import { validateFile } from '../../utils/file-validation';
import { TechniqueDefinitionsService, type TechniqueDefinition } from '../../services/technique-definitions.service';
import type { AnalysisSession, VerdictResult } from '../../types/analysis-session';
import type { InputType, ComponentStatus } from './types';
import { COMPONENT_ORDER } from './utils';
import {
  Container, Header, Title, Subtitle,
  ErrorBox, ErrorIcon,
  SkipBox, SkipText, SkipAction,
  WarningBox,
} from './styles';
import { InputForm } from './sections/InputForm';
import { InputPreview } from './sections/InputPreview';
import { LoadingState } from './sections/LoadingState';
import { Verdict } from './sections/Verdict';
import { DownloadPdfButton } from '../Reports/DownloadPdfButton';

// Re-exports — preserve backward-compat for ../pages/History which imports
// the detail renderers from this file path.
export { renderTechniquesDetail } from './details/techniques';
export { renderAiDetail } from './details/ai';
export { renderClaimsDetail } from './details/claims';
export { renderDomainDetail } from './details/domain';
export { renderSourceDetail } from './details/source';

export const PipelineAnalysis: React.FC = () => {
  const { t } = useTranslation();
  const async = useAsyncAnalysis('pipeline');

  const [inputType, setInputType] = useState<InputType>('text');
  const [inputText, setInputText] = useState('');
  const [inputUrl, setInputUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const [components, setComponents] = useState<Record<string, ComponentStatus>>({});
  const [verdict, setVerdict] = useState<VerdictResult | null>(null);
  const [fullResult, setFullResult] = useState<Record<string, any> | null>(null);
  const [analysisSession, setAnalysisSession] = useState<AnalysisSession | null>(null);
  const [totalDuration, setTotalDuration] = useState<number | null>(null);
  const [expandedComponents, setExpandedComponents] = useState<Set<string>>(new Set());
  const [techniqueDefinitions, setTechniqueDefinitions] = useState<TechniqueDefinition[]>([]);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  // Snapshot of what was actually submitted (separate from current textarea).
  const [analyzedInput, setAnalyzedInput] = useState<{ type: InputType; text: string } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load technique definitions once (cached in service)
  useEffect(() => {
    TechniqueDefinitionsService.getDefinitions()
      .then(setTechniqueDefinitions)
      .catch(err => console.warn('Failed to load technique definitions:', err));
  }, []);

  // Track when analysis starts so LoadingState can show elapsed time.
  useEffect(() => {
    if (async.isAnalyzing && !startedAt) setStartedAt(Date.now());
    if (!async.isAnalyzing && startedAt && async.result) {
      // keep startedAt around — totalDuration replaces it post-finish
    }
  }, [async.isAnalyzing, async.result, startedAt]);

  // Final result arrived (AnalysisSession)
  useEffect(() => {
    if (!async.result) return;
    const data: AnalysisSession = async.result;
    setAnalysisSession(data);
    if (data.verdict) setVerdict(data.verdict);

    const compData: Record<string, any> = {};
    if (data.techniques) compData.techniques = data.techniques;
    if (data.ai_tampered) compData.ai_tampered = data.ai_tampered;
    if (data.claims) compData.claims = data.claims;
    if (data.domain) compData.domain = data.domain;
    if (data.source_assessment) compData.source_assessment = data.source_assessment;
    if (Object.keys(compData).length > 0) setFullResult(compData);
    if (data.total_duration_ms) setTotalDuration(data.total_duration_ms);

    if (data.components_run) {
      const updated: Record<string, ComponentStatus> = {};
      for (const comp of data.components_run) {
        const key = comp === 'domain' && data.source_assessment ? 'source_assessment' : comp;
        updated[key] = { status: 'completed' };
      }
      for (const comp of data.components_skipped || []) {
        const key = comp === 'domain' && data.source_assessment ? 'source_assessment' : comp;
        updated[key] = { status: 'skipped' };
      }
      setComponents(updated);
    }
  }, [async.result]);

  // Progressive results from polling — update component statuses + partial results
  useEffect(() => {
    if (!async.session) return;
    const session: AnalysisSession = async.session;

    setComponents(prev => {
      const updated: Record<string, ComponentStatus> = { ...prev };
      const mapComp = (c: string) => c === 'domain' && session.source_assessment ? 'source_assessment' : c;

      for (const comp of session.components_run || []) {
        const key = mapComp(comp);
        updated[key] = { ...(updated[key] || {}), status: 'completed' };
      }

      // Mark next-in-line components as running
      if (session.status === 'running' && session._queue) {
        const total = session._queue.total_components;
        const done = (session.components_run || []).length;
        const remaining = total - done;
        let runningCount = 0;
        const mappedRun = (session.components_run || []).map(mapComp);
        for (const comp of COMPONENT_ORDER) {
          if (mappedRun.includes(comp)) continue;
          if (runningCount >= remaining) break;
          if (!updated[comp] || updated[comp].status !== 'completed') {
            updated[comp] = { ...(updated[comp] || {}), status: 'running' };
          }
          runningCount++;
        }
      }
      return updated;
    });

    const compData: Record<string, any> = {};
    if (session.techniques) compData.techniques = session.techniques;
    if (session.ai_tampered) compData.ai_tampered = session.ai_tampered;
    if (session.claims) compData.claims = session.claims;
    if (session.domain) compData.domain = session.domain;
    if (session.source_assessment) compData.source_assessment = session.source_assessment;
    if (Object.keys(compData).length > 0) {
      setFullResult(prev => ({ ...prev, ...compData }));
    }
  }, [async.session]);

  const resetResults = () => {
    setComponents({});
    setVerdict(null);
    setFullResult(null);
    setAnalysisSession(null);
    setTotalDuration(null);
    setExpandedComponents(new Set());
    setStartedAt(null);
    setAnalyzedInput(null);
    async.reset();
  };

  const handleTypeChange = (type: InputType) => {
    setInputType(type);
    setSelectedFile(null);
    setInputUrl('');
    resetResults();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateFile(file, inputType);
    if (!validation.valid) {
      setFileError(validation.error);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setFileError(null);
    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAnalyze = () => {
    resetResults();
    setStartedAt(Date.now());
    if (inputType === 'text') {
      const txt = inputText.trim();
      setAnalyzedInput({ type: inputType, text: txt });
      async.submitText(txt);
    } else if (inputType === 'url') {
      const url = inputUrl.trim();
      setAnalyzedInput({ type: inputType, text: url });
      async.submitUrl(url);
    } else if (selectedFile) {
      setAnalyzedInput({ type: inputType, text: selectedFile.name });
      async.submitMedia(selectedFile, inputType);
    }
  };

  const toggleComponent = (name: string) => {
    setExpandedComponents(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const isRunning = async.isAnalyzing;
  const error = async.error;
  const progress = async.progress;
  const { skipped, skipMessage, warnings, statusText } = async;

  return (
    <Container>
      <Header>
        <Title>{t('pipeline.title')}</Title>
        <Subtitle>{t('pipeline.subtitle')}</Subtitle>
      </Header>

      <InputForm
        inputType={inputType}
        onTypeChange={handleTypeChange}
        inputText={inputText}
        onTextChange={setInputText}
        inputUrl={inputUrl}
        onUrlChange={setInputUrl}
        selectedFile={selectedFile}
        fileError={fileError}
        fileInputRef={fileInputRef}
        onFileSelect={handleFileSelect}
        onRemoveFile={handleRemoveFile}
        isRunning={isRunning}
        statusMsg={statusText}
        onAnalyze={handleAnalyze}
      />

      {error && <ErrorBox><ErrorIcon>!</ErrorIcon>{error}</ErrorBox>}

      {skipped && skipMessage && (
        <SkipBox>
          <SkipText>{skipMessage}</SkipText>
          <SkipAction onClick={() => { async.reset(); handleTypeChange('image'); }}>
            {t('common.uploadManual')}
          </SkipAction>
        </SkipBox>
      )}

      {warnings.length > 0 && (
        <WarningBox>
          {warnings.map((w, i) => <div key={i}>{w}</div>)}
        </WarningBox>
      )}

      {(isRunning || progress > 0) && !verdict && (
        <LoadingState
          progress={progress}
          components={components}
          fullResult={fullResult}
          startedAt={startedAt}
          statusMsg={statusText}
        />
      )}

      {verdict && analyzedInput && (
        <InputPreview inputType={analyzedInput.type} text={analyzedInput.text} />
      )}

      {verdict && analysisSession && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <DownloadPdfButton session={analysisSession} techDefs={techniqueDefinitions} />
        </div>
      )}

      {verdict && (
        <Verdict
          verdict={verdict}
          totalDuration={totalDuration}
          components={components}
          fullResult={fullResult}
          techniqueDefinitions={techniqueDefinitions}
        />
      )}
    </Container>
  );
};
